import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { PRODUCTS, WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import ProductFormGroupTab from './ProductFormGroupTab';
import ProductFormInfoTab from './ProductFormInfoTab';
import SaleChannelInventorySync from './SaleChannelInventorySync';
import { setCurrentTab, setProductGroupList } from './productGroupsSlice';

/**
 *
 * @author  @omerrkosar
 */

const ProductForm = NiceModal.create(({ id, mode, modalName, setProduct, isImportOrder }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();
	const { t } = useTranslation();
	const { showSuccess, showError } = useAlert();
	const user = useSelector((state) => state.user);
	const isAuthorized = useAccessControl();
	const defaultCurrency = user.localization.currency ?? '';
	const [isMinimizing, setIsMinimizing] = useState(false);
	const [errorTabs, setErrorTabs] = useState([]);
	const schema = useMemo(
		() =>
			setProduct
				? yup
						.object()
						.shape({
							// product Details
							name: yup.string().trim().min(3).max(255).nullable().required(),
							SKU: yup.string().nullable().required(),
							price: yup.number().nullable().required(),
							taxAmount: yup.number().nullable(),
							currency: yup.string().nullable().required()
						})
						.required()
				: yup.object().shape({
						// product Details
						name: yup.string().trim().min(3).max(255).nullable().required(),
						SKU: yup.string().nullable().required(),
						price: yup.number().nullable().required(),
						taxAmount: yup.number().nullable(),
						hsCode: yup.string().nullable(),
						currency: yup.string().nullable().required(),
						barcode: yup.string().nullable(),
						secondBarcode: yup.array().nullable(),
						imageURL: yup.string().nullable(),
						category: yup.string().nullable(),
						categoryId: yup.number().nullable(),
						serialRequirement: yup.string().nullable(),
						serialType: yup.string().nullable(),
						priority: yup.number().nullable(),
						brandID: yup
							.number()
							.nullable()
							.when([], {
								is: () => user.userType === 'BrandManager',
								then: (schema) => schema.required()
							}),
						description: yup.string().nullable(),
						preorder: yup.boolean().nullable(),
						bundleProduct: yup.boolean().nullable(),
						products: yup
							.array()
							.nullable()
							.when('bundleProduct', {
								is: true,
								then: (schema) =>
									schema.of(
										yup.object().shape({
											productID: yup.number().nullable().required(),
											quantity: yup.number().nullable().required(),
											sku: yup.string().nullable().required()
										})
									)
							}),
						isSkuEditable: yup.boolean().nullable(),
						// product Dimensions
						height: yup.number().nullable(),
						width: yup.number().nullable(),
						length: yup.number().nullable(),
						weight: yup.number().nullable(),
						cubicMeter: yup.number().nullable(),
						sellOnOTOStore: yup.boolean().nullable(),
						sellingPrice: yup.number().nullable(),
						productSalesChannelList: yup
							.array()
							.nullable()
							.of(
								yup.object().shape({
									salesChannelCredentialsID: yup.number().nullable(),
									inventoryCalculateMethod: yup.string().nullable(),
									salesChannelProductID: yup.string().nullable(),
									inventorySync: yup.boolean().nullable(),
									salesChannelCategoryId: yup.string().nullable(),
									salesChannelCategoryName: yup.string().nullable()
								})
							),
						attributes: yup
							.array()
							.of(
								yup.object().shape({
									attributeName: yup.string().nullable().required(),
									attributeValue: yup.string().nullable()
								})
							)
							.nullable()
					}),
		[setProduct, user]
	);

	const viewMode = mode === 'view';

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		if (!id) {
			return {
				name: null,
				SKU: null,
				price: null,
				taxAmount: null,
				hsCode: null,
				currency: isImportOrder ? 'CNY' : defaultCurrency,
				barcode: null,
				secondBarcode: null,
				imageURL: null,
				category: null,
				categoryId: null,
				brandID: null,
				description: null,
				preorder: false,
				bundleProduct: null,
				serialRequirement: null,
				serialType: null,
				priority: null,
				products: [{}],
				length: null,
				width: null,
				height: null,
				weight: null,
				cubicMeter: null,
				sellOnOTOStore: false,
				sellingPrice: null,
				productSalesChannelList: [
					{
						inventorySync: false,
						salesChannelCredentialsID: null,
						salesChannelProductID: null,
						inventoryCalculateMethod: null,
						salesChannelCategoryId: null,
						salesChannelCategoryName: null
					}
				],
				attributes: [],
				isSkuEditable: true
			};
		}

		return axiosPrivate
			.post(PRODUCTS.GET_PRODUCT, {
				ID: id
			})
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					// reset form fields

					return {
						name: data.name ?? null,
						SKU: data.SKU ?? null,
						price: data.price ?? null,
						taxAmount: data.taxAmount ?? null,
						hsCode: data.hsCode ?? null,
						currency: data.currency ?? defaultCurrency,
						barcode: data.barcode ?? null,
						secondBarcode: data.secondBarcode?.split(',').map((a) => a.trim()) ?? [],
						imageURL: data.imageURL ?? null,
						category: data.category ?? null,
						categoryId: data.categoryId ?? null,
						serialRequirement: data.serialRequirement ?? null,
						serialType: data.serialType ?? null,
						priority: data.priority ?? null,
						brandID: data.brandID ?? null,
						description: data.description ?? null,
						preorder: data.preorder ?? false,
						bundleProduct: data.bundleProduct ?? false,
						products: data.products
							? data.products.map((a) => ({
									sku: a.SKU,
									productID: a.productID,
									quantity: a.quantity
								}))
							: [{}],
						isSkuEditable: data.isSkuEditable ?? false,
						// product Dimensions
						width: data.width ?? null,
						height: data.height ?? null,
						length: data.length ?? null,
						weight: data.weight ?? null,
						cubicMeter: data.cubicMeter ?? null,
						sellOnOTOStore: data.sellOnOTOStore ?? false,
						sellingPrice: data.sellingPrice ?? null,

						// sales channel sync
						productSalesChannelList: data?.productSalesChannelList?.map((item) => ({
							ID: item?.ID ?? null,
							inventorySync: item.inventorySync ?? false,
							salesChannelCredentialsID: item.salesChannelCredentialsID ?? null,
							salesChannelProductID: item.salesChannelProductID ?? null,
							inventoryCalculateMethod: item.inventoryCalculateMethod ?? null,
							salesChannelCategoryId: item.salesChannelCategoryId ?? null,
							salesChannelCategoryName: item.salesChannelCategoryName ?? null
						})),

						attributes: data.attributes ?? []
					};
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});
	}, [id]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		getValues,
		control,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const { append: appendAttribute } = useFieldArray({ name: 'attributes', control });

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !viewMode && !isMinimizing);

	const [groups, setGroups] = useState([]);

	useEffect(() => {
		if (!isAuthorized('PIMModule')) return;
		axiosPrivate.post(WMS_MODULE.GET_ATTRIBUTES_GROUPS).then((response) => {
			if (response.status === 200) {
				setGroups(response.data);
				const existingAttributes = getValues('attributes').map((attribute) => attribute.attributeName);
				Object.values(response.data).forEach((group) => {
					group.forEach((attribute) => {
						if (existingAttributes.includes(attribute.attributeName)) return;
						if (attribute.type === 'Dropdown' || attribute.type === 'Date') return;
						appendAttribute({
							attributeName: attribute.attributeName,
							attributeValue: attribute.defaultValue ?? ''
						});
					});
				});
				dispatch(setProductGroupList(response.data));
			}
		});
	}, []);

	const onSubmit = (data) => {
		// Url check
		const URL = id ? PRODUCTS.UPDATE_PRODUCT : PRODUCTS.ADD_PRODUCT;
		const filteredAttributes = data.attributes.filter(
			(attribute) =>
				attribute.attributeValue !== '' &&
				attribute.attributeValue !== null &&
				attribute.attributeValue !== undefined
		);

		axiosPrivate
			.post(URL, {
				ID: id,
				name: data.name,
				SKU: data.SKU,
				price: data.price.toFixed(2),
				taxAmount: data.taxAmount,
				hsCode: data.hsCode,
				currency: data.currency,
				barcode: data.barcode,
				secondBarcode: data.secondBarcode?.join(','),

				imageURL: data.imageURL,
				category: data.category,
				categoryId: data.categoryId,
				serialRequirement: data.serialRequirement ? data.serialRequirement : null,
				serialType: data?.serialType,
				priority: data.priority,
				brandID: data.brandID,
				description: data.description,
				preorder: data.preorder,
				bundleProduct: data.bundleProduct,
				products: data.bundleProduct ? data.products : null,
				// product Dimensions
				height: data.height,
				length: data.length,
				weight: data.weight,
				width: data.width,
				cubicMeter: data.cubicMeter,
				sellOnOTOStore: data.sellOnOTOStore,
				sellingPrice: data.sellingPrice,
				productSalesChannelList: data.productSalesChannelList?.map((item) => ({
					ID: item?.ID ?? null,
					inventorySync: item.inventorySync,
					salesChannelCredentialsID: item.salesChannelCredentialsID,
					salesChannelProductID: item.salesChannelProductID,
					inventoryCalculateMethod: item.inventoryCalculateMethod,
					salesChannelCategoryId: item.salesChannelCategoryId,
					salesChannelCategoryName: item.salesChannelCategoryName
				})),
				attributes: filteredAttributes
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					showSuccess();
					setProduct && setProduct(response.data.ID);
					dispatch(updateDynamicListsInPage());
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.status === 406) {
					showError({
						header: 'error.F010',
						message: 'skuAlreadyExists'
					});
					return;
				}
				AppLogger.error(error);
				showError();
			});
	};

	const tabs = useMemo(
		() => [
			{
				name: PATHS.PRODUCTS,
				label: 'products',
				component: () => <ProductFormInfoTab setProduct={setProduct} />,
				fields: [
					'name',
					'SKU',
					'price',
					'taxAmount',
					'hsCode',
					'currency',
					'barcode',
					'secondBarcode',
					'imageURL',
					'category',
					'serialRequirement',
					'serialType',
					'brandID',
					'description',
					'preorder',
					'bundleProduct',
					'products',
					'isSkuEditable',
					'height',
					'width',
					'length',
					'weight',
					'cubicMeter',
					'sellOnOTOStore',
					'sellingPrice'
				],
				error: errorTabs.includes('products')
			},
			...(isAuthorized('PIMModule')
				? [
						{
							label: 'salesChannelSync',
							component: () => <SaleChannelInventorySync mode={mode} id={id} />,
							fields: ['productSalesChannelList'],
							error: errorTabs.includes('salesChannelSync')
						}
					]
				: []),

			...Object.keys(groups).map((group) => ({
				name: group,
				label: group,
				component: () => <ProductFormGroupTab />,
				fields: ['attributes'],
				error: errorTabs.includes(group)
			}))
		],
		[groups, errorTabs]
	);

	const onError = (errors) => {
		AppLogger.log(errors);
		const errorTabs = [];

		tabs.forEach((tab) => {
			if (tab.fields?.some((field) => errors[field])) {
				errorTabs.push(tab.label);
			}
		});
		if (errorTabs.length > 0) {
			showError({
				header: t('error.validationError'),
				message: t('error.tabsHaveErrors', {
					tabNames: errorTabs.map((tab) => t(tab)).join(', '),
					count: errorTabs.length
				})
			});
		}
		setErrorTabs(errorTabs);
	};

	return (
		<Modal
			fullwidth
			maxWidth="large"
			name={modalName}
			className="modal-product"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						setIsMinimizing(false);
						show();
					}}
					onClose={close}
					label={mode === 'edit' ? 'editProduct' : mode === 'view' ? 'viewProduct' : 'addProduct'}
					tooltip={['edit', 'view'].includes(mode) ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'edit' ? 'editProduct' : mode === 'view' ? 'viewProduct' : 'addProduct'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless viewPrintButton={viewMode}>
					<Tabs data={tabs} setIndex={(index) => dispatch(setCurrentTab(tabs[index].name))} />
				</Form>
			</ModalContent>

			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />

					<Button
						loading={isLoading}
						onClick={handleSubmit(onSubmit, onError)}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label={mode === 'edit' ? 'save' : 'button.add'}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default ProductForm;
